import { autocompletion, Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { Diagnostic, lintGutter, setDiagnostics } from '@codemirror/lint';
import { Compartment, EditorState, Extension } from '@codemirror/state';
import { EditorView, hoverTooltip, Tooltip } from '@codemirror/view';
import { csharp } from '@codemirror/legacy-modes/mode/clike';
import { basicSetup } from 'codemirror';
import { CodeSnippet } from '../../../../administration/models';
import {
    formatHoverContents,
    getCompletionReplacement,
    offsetToPosition,
    toCodeMirrorCompletion,
    toCodeMirrorDiagnostic,
    toTooltipBounds
} from './CSharpCodeEditorUtils';
import { CSharpLspClient } from '../lsp/CSharpLspClient';
import { CSharpCodeEditorReadyHandle, CSharpCodeEditorViewOptions } from '../types';

type RuntimeInitialization = {
    onChange?: (value: string) => void;
    readOnly: boolean;
    snippets: CodeSnippet[];
    value: string;
};

type EditorRuntimeState = {
    applyingExternalValue: boolean;
    currentValue: string;
    editor?: EditorView;
    editorOptions: CSharpCodeEditorViewOptions;
    editableCompartment: Compartment;
    hostElement?: HTMLDivElement;
    onChange?: (value: string) => void;
    reloadingDocument: boolean;
    snippets: CodeSnippet[];
};

type CSharpCodeEditorRuntimeOptions = {
    createUrl: () => string;
};

const rootUri = 'file:///src/JE.Alpha.DotNet.Shared';
const workspaceName = 'JE.Alpha.DotNet.Shared';
const initialWarmupTimeoutMs = 3000;
const defaultEditorOptions: CSharpCodeEditorViewOptions = {
    automaticLayout: true,
    readOnly: false
};

type PendingWarmup = {
    resolve: () => void;
    timeoutHandle: number;
};

export class CSharpCodeEditorRuntime {
    private client?: CSharpLspClient;

    private pendingWarmup?: PendingWarmup;

    private readonly state: EditorRuntimeState = CSharpCodeEditorRuntime.createEditorRuntimeState();

    private readonly readyHandle: CSharpCodeEditorReadyHandle = {
        focus: () => {
            this.state.editor?.focus();
        },
        layout: () => {
            this.state.editor?.requestMeasure();
        },
        updateOptions: options => {
            this.applyViewOptions(options);
        }
    };

    constructor(private readonly options: CSharpCodeEditorRuntimeOptions) {}

    private static buildSnippetCompletions(snippets: CodeSnippet[]): Completion[] {
        return (snippets || []).map(snippet => ({
            apply: snippet.code,
            detail: snippet.description || '',
            label: `${snippet.type}.${snippet.fullSnippetGroupPath || ''}${snippet.name}`,
            type: 'keyword'
        }));
    }

    private static createEditorRuntimeState(): EditorRuntimeState {
        return {
            applyingExternalValue: false,
            currentValue: '',
            editorOptions: {
                ...defaultEditorOptions
            },
            editableCompartment: new Compartment(),
            reloadingDocument: false,
            snippets: []
        };
    }

    private static dedupeSnippetCompletions(options: Completion[]): Completion[] {
        const seen = new Set<string>();

        return options.filter(option => {
            const apply = typeof option.apply === 'string' ? option.apply : option.label;
            const key = `${option.label}:${apply}`;
            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    }

    attachToContainer(container: HTMLDivElement): HTMLDivElement {
        this.state.hostElement = container;

        if (this.state.editor && this.state.editor.dom.parentElement !== container) {
            container.appendChild(this.state.editor.dom);
            this.state.editor.requestMeasure();
        }

        return container;
    }

    async initialize(options: RuntimeInitialization): Promise<CSharpCodeEditorReadyHandle> {
        this.updateOnChange(options.onChange);
        this.updateSnippets(options.snippets);
        this.updateReadOnly(options.readOnly);
        this.updateValue(options.value);
        this.getOrCreateEditor();

        const warmupPromise = this.startWarmupWait();
        const openDocumentPromise = this.openDocument(options.value).catch(() => undefined);
        await Promise.all([warmupPromise, openDocumentPromise]);
        return this.readyHandle;
    }

    updateValue(value: string): void {
        const nextValue = value ?? '';
        this.state.currentValue = nextValue;

        if (!this.state.editor || this.state.editor.state.doc.toString() === nextValue) {
            return;
        }

        this.state.applyingExternalValue = true;
        this.state.editor.dispatch({
            changes: {
                from: 0,
                insert: nextValue,
                to: this.state.editor.state.doc.length
            }
        });

        this.state.applyingExternalValue = false;

        if (!this.state.reloadingDocument && this.client) {
            this.syncDocumentInBackground(nextValue);
        }
    }

    updateReadOnly(readOnly: boolean): void {
        this.applyViewOptions({
            readOnly
        });
    }

    updateSnippets(snippets: CodeSnippet[]): void {
        this.state.snippets = snippets || [];
    }

    updateOnChange(onChange?: (value: string) => void): void {
        this.state.onChange = onChange;
    }

    layout(): void {
        this.state.editor?.requestMeasure();
    }

    release(container?: HTMLDivElement): void {
        if (container && this.state.hostElement && this.state.hostElement !== container) {
            return;
        }

        if (this.state.editor?.dom.parentElement) {
            this.state.editor.dom.parentElement.removeChild(this.state.editor.dom);
        }

        this.state.hostElement = undefined;
        this.state.onChange = undefined;
    }

    dispose(): void {
        this.finishWarmupWait();
        this.client?.dispose();
        this.client = undefined;

        if (this.state.editor) {
            this.state.editor.destroy();
            this.state.editor = undefined;
        }

        this.state.hostElement = undefined;
        this.state.onChange = undefined;
    }

    private applyDiagnostics(diagnostics: Diagnostic[]) {
        if (!this.state.editor) {
            return;
        }

        const transaction = setDiagnostics(this.state.editor.state, diagnostics);
        this.state.editor.dispatch(transaction);
    }

    private applyViewOptions(options: CSharpCodeEditorViewOptions) {
        this.state.editorOptions = {
            ...this.state.editorOptions,
            ...options
        };

        if (!this.state.editor) {
            return;
        }

        this.state.editor.dispatch({
            effects: this.state.editableCompartment.reconfigure(
                this.createEditableExtension(Boolean(this.state.editorOptions.readOnly))
            )
        });

        if (this.state.editorOptions.automaticLayout) {
            this.state.editor.requestMeasure();
        }
    }

    private async completionSource(context: CompletionContext): Promise<CompletionResult | null> {
        const replacement = getCompletionReplacement(context);

        if (!replacement) {
            return null;
        }

        const snippetOptions = CSharpCodeEditorRuntime.dedupeSnippetCompletions(
            CSharpCodeEditorRuntime.buildSnippetCompletions(this.state.snippets)
        );

        let lspOptions: Completion[] = [];

        try {
            const items = await this.getOrCreateClient().requestCompletion(
                offsetToPosition(context.state, context.pos)
            );

            lspOptions = items.map(item => toCodeMirrorCompletion(item));
        } catch (_error) {
            lspOptions = [];
        }

        const options = [...snippetOptions, ...lspOptions];

        if (!options.length) {
            return null;
        }

        return {
            from: replacement.from,
            options,
            validFor: replacement.validFor
        };
    }

    private createEditableExtension(readOnly: boolean): Extension {
        return [EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)];
    }

    private createExtensions(): Extension[] {
        return [
            basicSetup,
            lintGutter(),
            StreamLanguage.define(csharp),
            this.state.editableCompartment.of(this.createEditableExtension(Boolean(this.state.editorOptions.readOnly))),
            EditorView.theme({
                '&': {
                    height: '100%'
                },
                '.cm-editor': {
                    height: '100%'
                },
                '.cm-scroller': {
                    fontFamily: 'Consolas, "Courier New", monospace',
                    overflow: 'auto'
                },
                '.cm-tooltip': {
                    border: '1px solid #d9dbe7',
                    borderRadius: '4px',
                    maxWidth: '420px',
                    padding: '8px 12px'
                }
            }),
            autocompletion({
                override: [context => this.completionSource(context)]
            }),
            hoverTooltip((view, pos) => this.createHoverTooltip(view, pos), {
                hideOnChange: true,
                hoverTime: 250
            }),
            EditorView.updateListener.of(update => {
                if (!update.docChanged) {
                    return;
                }

                const nextValue = update.state.doc.toString();
                this.state.currentValue = nextValue;

                if (!this.state.applyingExternalValue) {
                    this.state.onChange?.(nextValue);

                    if (this.client) {
                        this.syncDocumentInBackground(nextValue);
                    }
                }
            })
        ];
    }

    private createHoverTooltip(view: EditorView, pos: number): Promise<Tooltip | null> {
        return this.getOrCreateClient()
            .requestHover(offsetToPosition(view.state, pos))
            .then(hover => {
                const hoverText = formatHoverContents(hover ? hover.contents : undefined);

                if (!hoverText) {
                    return null;
                }

                const bounds = toTooltipBounds(view.state, hover ? hover.range : undefined, pos);

                return {
                    above: true,
                    end: bounds.end,
                    pos: bounds.pos,
                    strictSide: false,
                    create: () => {
                        const dom = document.createElement('div');
                        dom.style.whiteSpace = 'pre-wrap';
                        dom.textContent = hoverText;
                        return { dom };
                    }
                };
            })
            .catch(() => null);
    }

    private getOrCreateEditor(): EditorView {
        if (!this.state.hostElement) {
            throw new Error('Editor container must be attached before initialization');
        }

        if (!this.state.editor) {
            this.state.editor = new EditorView({
                parent: this.state.hostElement,
                state: EditorState.create({
                    doc: this.state.currentValue,
                    extensions: this.createExtensions()
                })
            });
        }

        return this.state.editor;
    }

    private getOrCreateClient(): CSharpLspClient {
        if (!this.client) {
            this.client = new CSharpLspClient({
                createUrl: this.options.createUrl,
                onDiagnostics: (uri, diagnostics) => {
                    if (uri !== this.client?.activeUri) {
                        return;
                    }

                    this.applyDiagnostics(
                        diagnostics.map(diagnostic => toCodeMirrorDiagnostic(this.state.currentValue, diagnostic))
                    );
                },
                onDocumentWarm: uri => {
                    if (uri !== this.client?.activeUri) {
                        return;
                    }

                    this.resolveWarmup();
                },
                rootUri,
                workspaceName
            });
        }

        return this.client;
    }

    private async openDocument(code: string): Promise<void> {
        this.state.currentValue = code ?? '';
        this.state.reloadingDocument = true;
        this.updateValue(this.state.currentValue);
        this.state.reloadingDocument = false;
        await this.getOrCreateClient().openDocument(this.state.currentValue);
    }

    private syncDocumentInBackground(value: string): void {
        if (!this.client) {
            return;
        }

        void this.client.syncDocument(value).catch(() => undefined);
    }

    private resolveWarmup(): void {
        if (!this.pendingWarmup) {
            return;
        }

        this.finishWarmupWait();
    }

    private startWarmupWait(): Promise<void> {
        this.finishWarmupWait();

        return new Promise(resolve => {
            const timeoutHandle = window.setTimeout(() => {
                if (this.pendingWarmup?.timeoutHandle !== timeoutHandle) {
                    return;
                }

                this.finishWarmupWait();
            }, initialWarmupTimeoutMs);

            this.pendingWarmup = {
                resolve,
                timeoutHandle
            };
        });
    }

    private finishWarmupWait(): void {
        if (!this.pendingWarmup) {
            return;
        }

        const { resolve, timeoutHandle } = this.pendingWarmup;

        this.pendingWarmup = undefined;
        window.clearTimeout(timeoutHandle);
        resolve();
    }
}
